import { createApi } from "@reduxjs/toolkit/query/react";

import {
    IAnalyzeProgressBody,
    IAnalyzeProgressResponse,
    IGetClientByIdQuery,
    IGetClientByIdResponse,
    IGetClientModuleRelevancyReportsQuery,
    IGetClientModuleRelevancyReportsResponse,
    IGetClientModulesReportsQuery,
    IGetClientModulesReportsResponse,
    IGetClientStatementsReportsQuery,
    IGetClientStatementsReportsResponse,
    IGetClientNotesQuery,
    IGetClientNotesResponse,
    IGetClientNotificationsQuery,
    IGetClientNotificationsResponse,
    IGetClientProgressQuery,
    IGetClientProgressResponse,
    IGetClientRelaxationReportsQuery,
    IGetClientRelaxationReportsResponse,
    IGetClientsQuery,
    IGetClientsResponse,
    IGetClientTopicRelevancyReportsQuery,
    IGetClientTopicRelevancyReportsResponse,
    IGetClientUsedTokensQuery,
    IGetClientUsedTokensResponse,
    IGetPartnerReportsQuery,
    IGetPartnerReportsResponse,
    ISaveProgressReportBody,
    ISaveProgressReportResponse,
    ISendNotificationsToClientBody,
    ISendNotificationsToClientResponse,
    ISetClientCurrentExerciseBody,
    ISetClientCurrentExerciseResponse,
    ISetClientPersonnelBody,
    ISetClientPersonnelResponse,
    ISetClientProjectBody,
    ISetClientProjectResponse,
    ISetReadNotificationsBody,
    ISetReadNotificationsResponse,
    IUpdateClientByIdBody,
    IUpdateClientByIdResponse,
    IUpdateClientNoteBody,
    UpdateClientNoteResponse,
    IGetAdminChangesQuery,
    IGetAdminChangesResponse,
    ICancelAssessmentPrescriptionBody,
    ICancelAssessmentPrescriptionResponse,
    ICreateAssessmentPrescriptionBody,
    ICreateAssessmentPrescriptionResponse,
    IGetAssessmentPrescriptionsQuery,
    IGetAssessmentPrescriptionsResponse,
    IUpdateAssessmentPrescriptionBody,
    IUpdateAssessmentPrescriptionResponse,
} from "../../types/clients";
import { getClientsQueryString, getClientStatementsReportsQueryString } from "../../utils/queries";
import { baseQueryWithIntercept } from "../connection/api";

const ENDPOINT_PREFIX = "clients";

export const clientsApi = createApi({
    reducerPath: "clientsApi",
    baseQuery: baseQueryWithIntercept,
    tagTypes: ["Clients", "Client", "Messages", "UsedTokens", "Project", "StatementReports", "Progress", "ModulesReports", "AssessmentPrescriptions"],
    endpoints: build => ({
        getClients: build.query<IGetClientsResponse, IGetClientsQuery>({
            query: query => ({
                url: `${ENDPOINT_PREFIX}?${getClientsQueryString(query)}`,
            }),
            providesTags: (result, _error, _arg) => (result ? [...result.clients.map(({ id }) => ({ type: "Clients" as const, id })), "Clients"] : ["Clients"]),
        }),
        getClientById: build.query<IGetClientByIdResponse, IGetClientByIdQuery>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/${data.clientId}`,
                method: "GET",
            }),
            providesTags: (_result, _error, arg) => [{ type: "Client", id: arg.clientId }],
        }),
        getClientNotifications: build.query<IGetClientNotificationsResponse, IGetClientNotificationsQuery>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/notifications/${data.clientId}`,
                method: "GET",
            }),
            providesTags: (_result, _error, arg) => [{ type: "Messages", id: arg.clientId }],
        }),
        getClientNotes: build.query<IGetClientNotesResponse, IGetClientNotesQuery>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/notes/${data.clientId}`,
                method: "GET",
            }),
        }),
        getClientUsedTokens: build.query<IGetClientUsedTokensResponse, IGetClientUsedTokensQuery>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/used-tokens/${data.clientId}`,
                method: "GET",
            }),
            providesTags: (_result, _error, arg) => [{ type: "UsedTokens", id: arg.clientId }],
        }),
        getClientRelaxationReports: build.query<IGetClientRelaxationReportsResponse, IGetClientRelaxationReportsQuery>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/relaxation-reports/${data.clientId}`,
                method: "GET",
            }),
            providesTags: (_result, _error, arg) => [{ type: "UsedTokens", id: arg.clientId }],
        }),
        getClientModulesReports: build.query<IGetClientModulesReportsResponse, IGetClientModulesReportsQuery>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/modules-reports/${data.clientId}`,
                method: "GET",
            }),
            providesTags: (_result, _error, arg) => [{ type: "ModulesReports", id: arg.clientId }],
        }),
        getClientStatementsReports: build.query<IGetClientStatementsReportsResponse, IGetClientStatementsReportsQuery>({
            query: data => {
                const qs = getClientStatementsReportsQueryString(data);
                return {
                    url: `${ENDPOINT_PREFIX}/statements-reports/${data.clientId}${qs ? `?${qs}` : ""}`,
                    method: "GET",
                };
            },
            providesTags: () => ["StatementReports"],
        }),
        getClientTopicRelevancyReports: build.query<IGetClientTopicRelevancyReportsResponse, IGetClientTopicRelevancyReportsQuery>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/topic-relevancy-reports/${data.clientId}`,
                method: "GET",
            }),
        }),
        getClientModuleRelevancyReports: build.query<IGetClientModuleRelevancyReportsResponse, IGetClientModuleRelevancyReportsQuery>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/module-relevancy-reports/${data.clientId}`,
                method: "GET",
            }),
        }),
        getClientProgress: build.query<IGetClientProgressResponse, IGetClientProgressQuery>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/client-progress/${data.clientId}`,
                method: "GET",
            }),
            providesTags: (_result, _error, arg) => [{ type: "Progress", id: arg.clientId }],
        }),
        getAdminChanges: build.query<IGetAdminChangesResponse, IGetAdminChangesQuery>({
            query: ({ clientId, since }) => {
                const params = new URLSearchParams({ clientId });
                if (since) params.set("since", since);
                return { url: `admin/changes?${params.toString()}`, method: "GET" };
            },
        }),
        getPartnerReports: build.query<IGetPartnerReportsResponse, IGetPartnerReportsQuery>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/partner-reports/${data.clientId}`,
                method: "GET",
            }),
        }),
        updateClientById: build.mutation<IUpdateClientByIdResponse, IUpdateClientByIdBody>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/${data.clientId}`,
                method: "PUT",
                body: data.data,
            }),
            invalidatesTags: (_result, _error, arg) => [{ type: "Client", id: arg.clientId }, "Clients", { type: "Project", id: arg.projectId }],
        }),
        setClientPersonnel: build.mutation<ISetClientPersonnelResponse, ISetClientPersonnelBody>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/set-client-personnel/${data.clientId}`,
                method: "PATCH",
                body: {
                    personnelIds: data.personnelIds,
                },
            }),
            invalidatesTags: (_result, _error, arg) => [{ type: "Client", id: arg.clientId }, "Clients"],
        }),
        setClientProject: build.mutation<ISetClientProjectResponse, ISetClientProjectBody>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/set-project/${data.clientId}`,
                method: "PATCH",
                body: {
                    project: data.projectId,
                },
            }),
            invalidatesTags: (_result, _error, arg) => [{ type: "Client", id: arg.clientId }, "Clients"],
        }),
        updateClientNote: build.mutation<UpdateClientNoteResponse, IUpdateClientNoteBody>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/notes/${data.noteId}`,
                method: "PATCH",
                body: {
                    note: data.data,
                },
            }),
            invalidatesTags: (_result, _error, _arg) => ["Clients"],
        }),
        sendNotificationsToUsers: build.mutation<ISendNotificationsToClientResponse, ISendNotificationsToClientBody>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/send-notification`,
                method: "POST",
                body: data,
            }),
            invalidatesTags: (_result, _error, arg) => [{ type: "Messages", id: arg.clientId }, "Messages"],
        }),
        setReadNotifications: build.mutation<ISetReadNotificationsResponse, ISetReadNotificationsBody>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/set-read-notification`,
                method: "POST",
                body: data,
            }),
            invalidatesTags: (_result, _error, arg) => [{ type: "Messages", id: arg.clientId }, "Messages", "Clients"],
        }),
        analyzeProgress: build.mutation<IAnalyzeProgressResponse, IAnalyzeProgressBody>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/analyze-progress`,
                method: "POST",
                body: data,
            }),
            invalidatesTags: [],
        }),
        saveProgressReport: build.mutation<ISaveProgressReportResponse, ISaveProgressReportBody>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/save-progress-report`,
                method: "POST",
                body: data,
            }),
            invalidatesTags: (_result, _error, arg) => [{ type: "Client", id: arg.clientId }, "Clients"],
        }),
        getAssessmentPrescriptions: build.query<IGetAssessmentPrescriptionsResponse, IGetAssessmentPrescriptionsQuery>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/assessment-prescriptions/${data.clientId}`,
                method: "GET",
            }),
            providesTags: (_result, _error, arg) => [{ type: "AssessmentPrescriptions", id: arg.clientId }],
        }),
        createAssessmentPrescription: build.mutation<ICreateAssessmentPrescriptionResponse, ICreateAssessmentPrescriptionBody>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/assessment-prescriptions/${data.clientId}`,
                method: "POST",
                body: data.data,
            }),
            invalidatesTags: (_result, _error, arg) => [{ type: "AssessmentPrescriptions", id: arg.clientId }],
        }),
        updateAssessmentPrescription: build.mutation<IUpdateAssessmentPrescriptionResponse, IUpdateAssessmentPrescriptionBody>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/assessment-prescriptions/${data.prescriptionId}`,
                method: "PATCH",
                body: data.data,
            }),
            invalidatesTags: (_result, _error, arg) => [{ type: "AssessmentPrescriptions", id: arg.clientId }],
        }),
        cancelAssessmentPrescription: build.mutation<ICancelAssessmentPrescriptionResponse, ICancelAssessmentPrescriptionBody>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/assessment-prescriptions/${data.prescriptionId}`,
                method: "DELETE",
            }),
            invalidatesTags: (_result, _error, arg) => [{ type: "AssessmentPrescriptions", id: arg.clientId }],
        }),
        setClientCurrentExercise: build.mutation<ISetClientCurrentExerciseResponse, ISetClientCurrentExerciseBody>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/set-current-exercise/${data.clientId}`,
                method: "PATCH",
                body: {
                    trackId: data.trackId,
                    moduleCode: data.moduleCode,
                    exerciseCode: data.exerciseCode,
                },
            }),
            invalidatesTags: (_result, _error, arg) => [{ type: "Client", id: arg.clientId }],
        }),
    }),
});

export const {
    useGetClientsQuery,
    useGetClientByIdQuery,
    useGetClientNotificationsQuery,
    useGetClientNotesQuery,
    useGetClientUsedTokensQuery,
    useGetClientModulesReportsQuery,
    useGetClientStatementsReportsQuery,
    useGetClientTopicRelevancyReportsQuery,
    useGetClientModuleRelevancyReportsQuery,
    useGetClientProgressQuery,
    useGetClientRelaxationReportsQuery,
    useGetPartnerReportsQuery,
    useAnalyzeProgressMutation,
    useUpdateClientByIdMutation,
    useUpdateClientNoteMutation,
    useSetClientPersonnelMutation,
    useSetClientProjectMutation,
    useSendNotificationsToUsersMutation,
    useSetReadNotificationsMutation,
    useSaveProgressReportMutation,
    useSetClientCurrentExerciseMutation,
    useGetAssessmentPrescriptionsQuery,
    useCreateAssessmentPrescriptionMutation,
    useUpdateAssessmentPrescriptionMutation,
    useCancelAssessmentPrescriptionMutation,
} = clientsApi;
